/**
 * Randevu Test - Tarayıcıdan teknisyen akışını test etmek için
 * Mobil ile aynı API'leri kullanır: PATCH .../update-status, OTP tamamlama, varsayılan test konumu
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBackRoute } from '../hooks/useBackRoute';
import { sanitizeString } from '../utils/security';
import IntegerInput from '../components/IntegerInput';
import { parseAdet } from '../utils/integerInput';
import {
  fetchAppointmentsByTechService,
  fetchAppointmentDetailed,
  updateAppointmentStatusTeknisyen,
  addAppointmentOperation,
  addAppointmentReplacedPart,
  addAppointmentIlaveIslem,
  addAppointmentIlaveParca,
  finishAppointment,
} from '../services/serviceRecordsService';
import { fetchTechniciansByTechService } from '../services/techniciansService';
import { generateOtp, verifyOtp } from '../services/mobileAppointmentService';
import ParcaSecModal from '../components/ParcaSecModal';
import IslemSecModal from '../components/IslemSecModal';

const STORAGE_KEY_LAT = 'randevu_test_default_lat';
const STORAGE_KEY_LNG = 'randevu_test_default_lng';
const DEFAULT_TEST_LAT = '38.50091217166824';
const DEFAULT_TEST_LNG = '27.091610529708003';

const safeText = (v) => sanitizeString(v ?? '');

const getErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  const mesaj = err?.data?.mesaj || err?.data?.message || err?.message || fallback;
  const hatalar = err?.data?.hatalar;
  if (Array.isArray(hatalar) && hatalar.length > 0) {
    const detay = hatalar.map((h) => (h.alan ? `${h.alan}: ${h.mesaj}` : h.mesaj)).join('; ');
    return detay ? `${mesaj}: ${detay}` : mesaj;
  }
  return mesaj;
};

const extractTechServiceId = (u) => {
  if (!u) return null;
  const ts = u.techService ?? u.techServiceId ?? u.teknikServisId;
  if (!ts) return null;
  if (typeof ts === 'string' && ts.trim()) return ts.trim();
  if (Array.isArray(ts) && ts.length > 0) {
    const first = ts[0];
    if (typeof first === 'string' && first.trim()) return first.trim();
    if (first && typeof first === 'object') {
      const id = first._id ?? first.id;
      return id ? String(id) : null;
    }
  }
  if (ts && typeof ts === 'object') {
    const id = ts._id ?? ts.id;
    return id ? String(id) : null;
  }
  return null;
};

const formatDate = (val) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR');
  } catch {
    return '—';
  }
};

const DURUM_LABELS = {
  'Randevu Tarihi Bekleniyor': { label: 'Tarih Bekleniyor', color: '#6b7280', bg: '#f3f4f6' },
  'Teknisyen Atandı': { label: 'Atandı', color: '#6b7280', bg: '#f3f4f6' },
  'İşlemde': { label: 'İşlemde', color: '#2563eb', bg: '#dbeafe' },
  'Randevu Tamamlandı': { label: 'Tamamlandı', color: '#059669', bg: '#d1fae5' },
  'Randevu İptal Edildi': { label: 'İptal', color: '#dc2626', bg: '#fee2e2' },
};

const RandevuTestPage = () => {
  const { user } = useAuth();
  const goBack = useBackRoute('/');
  const techServiceId = useMemo(() => extractTechServiceId(user), [user]);
  const isTechPerson = user?.rol === 'tech-person';
  const isTechService = user?.rol === 'tech-service' || user?.rol === 'mod' || user?.rol === 'admin';

  const [defaultLat, setDefaultLat] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_LAT) || DEFAULT_TEST_LAT;
    } catch {
      return DEFAULT_TEST_LAT;
    }
  });
  const [defaultLng, setDefaultLng] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_LNG) || DEFAULT_TEST_LNG;
    } catch {
      return DEFAULT_TEST_LNG;
    }
  });

  const saveDefaultLocation = useCallback((lat, lng) => {
    try {
      if (lat != null && lat !== '') localStorage.setItem(STORAGE_KEY_LAT, String(lat));
      if (lng != null && lng !== '') localStorage.setItem(STORAGE_KEY_LNG, String(lng));
    } catch (_) {}
  }, []);

  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loadingTech, setLoadingTech] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [aciklama, setAciklama] = useState('');
  const [garantiDahilinde, setGarantiDahilinde] = useState(true);
  const [musteriyeFaturaEdilen, setMusteriyeFaturaEdilen] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [finishAction, setFinishAction] = useState(null);
  const [submitError, setSubmitError] = useState('');

  const [newOperation, setNewOperation] = useState({ islemAdi: '', aciklama: '' });
  const [newPart, setNewPart] = useState({ parcaAdi: '', parcaKodu: '', adet: 1, fiyat: 0, aciklama: '' });
  const [newIlave, setNewIlave] = useState({ islemAdi: '', fiyat: 0, adet: 1, aciklama: '' });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [isParcaSecModalOpen, setIsParcaSecModalOpen] = useState(false);
  const [isIslemSecModalOpen, setIsIslemSecModalOpen] = useState(false);
  const photoInputRef = React.useRef(null);

  const effectiveTeknisyenId = isTechPerson ? user?.id ?? user?._id : selectedTechnicianId;

  const loadTechnicians = useCallback(async () => {
    if (!techServiceId) return;
    setLoadingTech(true);
    setError('');
    try {
      const res = await fetchTechniciansByTechService(techServiceId);
      const list = res?.veri ?? res?.data ?? [];
      setTechnicians(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(getErrorMessage(e, 'Teknisyenler yüklenemedi'));
    } finally {
      setLoadingTech(false);
    }
  }, [techServiceId]);

  const loadAppointments = useCallback(async () => {
    if (!techServiceId) return;
    if (!isTechPerson && !selectedTechnicianId) {
      setAppointments([]);
      return;
    }
    setLoadingAppointments(true);
    setError('');
    try {
      const params = { sayfa: 1, limit: 50, aktif: true };
      if (effectiveTeknisyenId) params.teknisyenId = effectiveTeknisyenId;
      const res = await fetchAppointmentsByTechService(techServiceId, params);
      const list = res?.veri?.randevular ?? res?.data?.randevular ?? [];
      setAppointments(Array.isArray(list) ? list : []);
      if (!selectedAppointment) return;
      const aptId = selectedAppointment?.id ?? selectedAppointment?._id;
      const found = list.find((a) => (a.id ?? a._id) === aptId);
      if (!found) setSelectedAppointment(null);
    } catch (e) {
      setError(getErrorMessage(e, 'Randevular yüklenemedi'));
    } finally {
      setLoadingAppointments(false);
    }
  }, [techServiceId, effectiveTeknisyenId, isTechPerson, selectedTechnicianId]);

  const getIdsFromAppointment = (apt) => {
    const data = apt?.randevu ?? apt;
    const srId = data?.servisKaydiId ?? apt?.servisKaydiId ?? apt?.servisKaydi?._id ?? apt?.servisKaydi?.id;
    const aptId = data?.id ?? data?._id ?? apt?.id ?? apt?._id;
    return { srId, aptId };
  };

  const loadAppointmentDetail = useCallback(async (apt) => {
    if (!apt) return;
    const { aptId } = getIdsFromAppointment(apt);
    if (!aptId) return;
    setLoadingDetail(true);
    setSubmitError('');
    try {
      const res = await fetchAppointmentDetailed(aptId);
      const veri = res?.veri ?? res?.data ?? res;
      setSelectedAppointment(veri ?? apt);
      setAciklama(veri?.aciklama ?? '');
      setGarantiDahilinde(veri?.garantiDahilinde !== false);
      setMusteriyeFaturaEdilen(veri?.musteriyeFaturaEdilen ?? '');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Randevu detayı yüklenemedi'));
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (techServiceId && isTechService) loadTechnicians();
  }, [techServiceId, isTechService, loadTechnicians]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleTechnicianChange = (e) => {
    setSelectedTechnicianId(e.target.value);
    setSelectedAppointment(null);
  };

  const handleSelectAppointment = (apt) => {
    setSelectedAppointment(null);
    loadAppointmentDetail(apt);
  };

  const handleIslemde = async () => {
    if (!selectedAppointment) return;
    const { aptId } = getIdsFromAppointment(selectedAppointment);
    if (!aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      // Mobil ile uyum: backend 'Islemde' (ASCII I) kabul eder, Türkçe İ encoding sorunlarını önler
      const updateRes = await updateAppointmentStatusTeknisyen(aptId, { yeniDurum: 'Islemde', notlar: 'Test: İşlemde' });
      const yeniDurum = updateRes?.veri?.yeniDurum ?? 'İşlemde';
      let merged = selectedAppointment;
      try {
        const refreshed = await fetchAppointmentDetailed(aptId);
        const veri = refreshed?.veri ?? refreshed?.data ?? refreshed;
        merged = { ...(veri ?? selectedAppointment), durum: veri?.durum ?? yeniDurum };
      } catch (_) {
        merged = { ...selectedAppointment, randevu: { ...(selectedAppointment?.randevu ?? selectedAppointment), durum: yeniDurum }, durum: yeniDurum };
      }
      setSelectedAppointment(merged);
      if (window.showNotification) window.showNotification('success', 'Durum: İşlemde');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Durum güncellenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddOperation = async () => {
    if (!selectedAppointment || !newOperation.islemAdi?.trim()) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await addAppointmentOperation(srId, aptId, {
        islemAdi: newOperation.islemAdi.trim(),
        aciklama: newOperation.aciklama?.trim() || undefined,
      });
      setNewOperation({ islemAdi: '', aciklama: '' });
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'Yapılan işlem eklendi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'İşlem eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleIslemSecilenleriEkle = async (secilenler) => {
    if (!selectedAppointment || !secilenler?.length) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      if (secilenler.length === 1) {
        await addAppointmentOperation(srId, aptId, {
          kategoriId: secilenler[0].kategoriId || undefined,
          islemAdi: secilenler[0].islemAdi,
          fiyat: Number(secilenler[0].fiyat) || 0,
          aciklama: secilenler[0].aciklama || undefined,
        });
      } else {
        await addAppointmentOperation(srId, aptId, {
          islemler: secilenler.map((s) => ({
            kategoriId: s.kategoriId || undefined,
            islemAdi: s.islemAdi,
            fiyat: Number(s.fiyat) || 0,
            aciklama: s.aciklama || undefined,
          })),
        });
      }
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', `${secilenler.length} işlem eklendi`);
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'İşlemler eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddPart = async () => {
    if (!selectedAppointment || !newPart.parcaAdi?.trim()) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await addAppointmentReplacedPart(srId, aptId, {
        parcaAdi: newPart.parcaAdi.trim(),
        parcaKodu: newPart.parcaKodu?.trim() || undefined,
        adet: parseAdet(newPart.adet, { min: 1 }),
        fiyat: Number(newPart.fiyat) || 0,
        aciklama: newPart.aciklama?.trim() || undefined,
      });
      setNewPart({ parcaAdi: '', parcaKodu: '', adet: 1, fiyat: 0, aciklama: '' });
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'Değiştirilen parça eklendi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Parça eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddIlaveIslem = async () => {
    if (!selectedAppointment || !newIlave.islemAdi?.trim()) return;
    const { aptId } = getIdsFromAppointment(selectedAppointment);
    if (!aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await addAppointmentIlaveIslem(aptId, {
        islemler: [{ islemAdi: newIlave.islemAdi.trim(), fiyat: Number(newIlave.fiyat) || 0, adet: parseAdet(newIlave.adet, { min: 1 }), aciklama: newIlave.aciklama?.trim() || undefined }],
      });
      setNewIlave({ islemAdi: '', fiyat: 0, adet: 1, aciklama: '' });
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'İlave işlem eklendi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'İlave işlem eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleParcaSecilenleriEkle = async (secilenler) => {
    if (!selectedAppointment || !secilenler?.length) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      for (const p of secilenler) {
        await addAppointmentReplacedPart(srId, aptId, {
          parcaAdi: p.parcaAdi || '',
          parcaKodu: p.parcaKodu || undefined,
          adet: p.adet || 1,
          fiyat: Number(p.fiyat) || 0,
        });
      }
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', `${secilenler.length} parça eklendi`);
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Parçalar eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const [otpLoading, setOtpLoading] = useState(false);

  const openFinishModal = async (action) => {
    setFinishAction(action);
    setOtpValue('');
    setSubmitError('');
    setShowOtpModal(true);
    if (!selectedAppointment) return;
    const { aptId } = getIdsFromAppointment(selectedAppointment);
    if (!aptId) return;
    setOtpLoading(true);
    try {
      const genRes = await generateOtp(aptId);
      const raw = genRes?.veri?.otp?.otp;
      const otp = raw != null ? String(raw).replace(/\D/g, '').padStart(6, '0').slice(0, 6) : '';
      setOtpValue(otp);
    } catch (_) {
      setSubmitError('OTP alınamadı. Tekrar deneyin.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleFinishSubmit = async () => {
    if (!selectedAppointment) return;
    const { aptId, srId } = getIdsFromAppointment(selectedAppointment);
    if (!aptId) {
      setSubmitError('Randevu ID bulunamadı');
      return;
    }
    // Backend: tam 6 rakam (Joi pattern /^[0-9]{6}$/)
    const otpRaw = (otpValue ?? '').toString().trim();
    const otpToSend = otpRaw.replace(/\D/g, '').padStart(6, '0').slice(0, 6);
    if (!otpToSend) {
      setSubmitError('OTP kodu girin veya modal açıldığında otomatik dolacaktır.');
      return;
    }
    setActionLoading(true);
    setSubmitError('');
    try {
      const formData = new FormData();
      formData.append('otp', otpToSend);
      const sonrakiIslemVal = finishAction === 'yeniRandevu' ? 'Yeni Randevu Oluşturulsun' : 'Servis Kaydını Bitir';
      formData.append('sonrakiIslem', sonrakiIslemVal);
      formData.append('garantiDahilinde', String(garantiDahilinde));
      if (aciklama?.trim()) formData.append('notlar', aciklama.trim());
      if (musteriyeFaturaEdilen?.trim()) formData.append('musteriyeFaturaEdilen', musteriyeFaturaEdilen.trim());
      if (defaultLat) formData.append('latitude', defaultLat);
      if (defaultLng) formData.append('longitude', defaultLng);
      // Mobil app ile aynı format: kategoriId gönderilmez (FinishAppointmentScreen cleanedOperations/cleanedParts)
      const aptData = selectedAppointment?.randevu ?? selectedAppointment;
      const yapilanIslemler = aptData?.yapilanIslemler ?? [];
      const degistirilenParcalar = aptData?.degistirilenParcalar ?? [];
      const cleanedOperations = yapilanIslemler.map((o) => {
        const op = { islemAdi: o.islemAdi ?? o.icerikAdi ?? '' };
        if (o.islemKodu ?? o.icerikKodu) op.islemKodu = String(o.islemKodu ?? o.icerikKodu).trim();
        return op;
      });
      const cleanedParts = degistirilenParcalar.map((p) => {
        const part = { parcaAdi: p.parcaAdi ?? p.icerikAdi ?? '', adet: p.adet ?? 1 };
        if (p.parcaKodu ?? p.icerikKodu) part.parcaKodu = String(p.parcaKodu ?? p.icerikKodu).trim();
        return part;
      });
      formData.append('yapilanIslemler', JSON.stringify(cleanedOperations));
      formData.append('degistirilenParcalar', JSON.stringify(cleanedParts));
      photoFiles.forEach((file) => formData.append('photos', file));
      await verifyOtp(aptId, formData);
      setShowOtpModal(false);
      setFinishAction(null);
      setOtpValue('');
      setPhotoFiles([]);
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'Randevu tamamlandı');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Tamamlama başarısız'));
    } finally {
      setActionLoading(false);
    }
  };

  const apt = selectedAppointment;
  const aptData = apt?.randevu ?? apt;
  const servisKaydi = apt?.servisKaydi ?? {};
  const durum = aptData?.durum ?? '';
  const canGoIslemde =
    durum === 'Teknisyen Atandı' ||
    durum === 'Randevu Tarihi Bekleniyor' ||
    durum === 'Randevu Onaylandı';
  const isIslemde = durum === 'İşlemde';
  const isTamamlandi = durum === 'Randevu Tamamlandı';

  const yapilanIslemler = aptData?.yapilanIslemler ?? [];
  const degistirilenParcalar = aptData?.degistirilenParcalar ?? [];
  const ilaveIslemler = aptData?.ilaveIslemler ?? [];

  const techniciansFromAppointments = useMemo(() => {
    if (technicians.length > 0) return [];
    const seen = new Set();
    return appointments.reduce((acc, a) => {
      const t = a.teknisyen;
      if (!t) return acc;
      const id = t.teknisyenId ?? t.id ?? t._id;
      if (!id || seen.has(id)) return acc;
      seen.add(id);
      acc.push({ id, _id: id, ad: t.ad ?? t.adi ?? '', soyad: t.soyad ?? t.soyadi ?? '' });
      return acc;
    }, []);
  }, [technicians.length, appointments]);

  const technicianList = technicians.length > 0 ? technicians : techniciansFromAppointments;

  if (!techServiceId && !isTechPerson) {
    return (
      <div className="main-content-inner" style={{ padding: '24px' }}>
        <p style={{ color: '#dc2626' }}>Teknik servis bilgisi bulunamadı. Sadece tech-person veya tech-service ile giriş yapın.</p>
      </div>
    );
  }

  return (
    <div className="main-content-inner" style={{ padding: '24px', maxWidth: '900px' }}>
      <button type="button" className="rt-btn rt-btn-outline" onClick={goBack} style={{ marginBottom: '16px' }}>
        <i className="fas fa-arrow-left"></i>
        <span>Geri Dön</span>
      </button>
      <h1 style={{ marginBottom: '8px', fontSize: '1.5rem' }}>
        <i className="fas fa-vial" style={{ marginRight: '8px' }}></i>
        Randevu Test
      </h1>
      <p style={{ color: '#64748b', marginBottom: '24px' }}>
        Tarayıcıdan teknisyen akışını test edin. Mobil ile aynı API kullanılır (update-status, OTP). Varsayılan konum verify-otp ile her zaman gönderilir.
      </p>

      {/* Varsayılan test konumu */}
      <div style={{ marginBottom: '24px', padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
        <h3 style={{ marginBottom: '8px', fontSize: '1rem' }}>Varsayılan test konumu (verify-otp için kullanılır)</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Enlem"
            value={defaultLat}
            onChange={(e) => {
              const v = e.target.value;
              setDefaultLat(v);
              saveDefaultLocation(v, defaultLng);
            }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '120px' }}
          />
          <input
            type="text"
            placeholder="Boylam"
            value={defaultLng}
            onChange={(e) => {
              const v = e.target.value;
              setDefaultLng(v);
              saveDefaultLocation(defaultLat, v);
            }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '120px' }}
          />
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: '8px', marginBottom: '16px', color: '#dc2626' }}>
          {error}
        </div>
      )}

      {isTechService && (
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>Teknisyen Seçin</label>
          <select
            value={selectedTechnicianId}
            onChange={handleTechnicianChange}
            disabled={loadingTech}
            style={{ minWidth: '280px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          >
            <option value="">Teknisyen seçin</option>
            {technicianList.map((t) => (
              <option key={t.id || t._id} value={t.id || t._id}>
                {safeText(t.ad ?? t.adi)} {safeText(t.soyad ?? t.soyadi)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="randevu-test-section" style={{ marginBottom: '24px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: '12px', fontWeight: 600, color: '#1e293b' }}>Randevular</h2>
        {loadingAppointments ? (
          <p>Yükleniyor...</p>
        ) : appointments.length === 0 ? (
          <p style={{ color: '#64748b' }}>Randevu bulunamadı.</p>
        ) : (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
            <table className="admins-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Randevu No</th>
                  <th>Müşteri</th>
                  <th>Tarih</th>
                  <th>Durum</th>
                  <th style={{ width: '100px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a) => {
                  const sr = a.servisKaydi ?? {};
                  const musteri = sr.musteri ?? {};
                  const musteriAd = `${musteri.adi ?? musteri.name ?? ''} ${musteri.soyadi ?? musteri.surname ?? ''}`.trim() || '—';
                  const ds = DURUM_LABELS[a.durum] ?? { label: a.durum ?? '—', color: '#6b7280', bg: '#f3f4f6' };
                  return (
                    <tr
                      key={a.id ?? a._id}
                      onClick={() => handleSelectAppointment(a)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleSelectAppointment(a)}
                      style={{ cursor: 'pointer' }}
                      className="admins-table-clickable-row"
                    >
                      <td>{safeText(a.randevuNumarasi)}</td>
                      <td>{musteriAd}</td>
                      <td>{formatDate(a.randevuTarihi)} {a.randevuSaati || ''}</td>
                      <td>
                        <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '12px', background: ds.bg, color: ds.color }}>
                          {ds.label}
                        </span>
                      </td>
                      <td>
                        <button type="button" className="rt-btn rt-btn-primary rt-btn-sm" onClick={(e) => { e.stopPropagation(); handleSelectAppointment(a); }}>
                          Seç
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loadingDetail ? (
        <p>Randevu detayı yükleniyor...</p>
      ) : apt ? (
        <div className="randevu-test-detail" style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', background: '#fff', marginTop: '8px' }}>
          <h2 style={{ marginBottom: '16px', fontSize: '1.15rem', fontWeight: 600, color: '#1e293b' }}>Randevu Detayı – {safeText(aptData?.randevuNumarasi)}</h2>
          {submitError && (
            <div style={{ padding: '12px', background: '#fef2f2', borderRadius: '8px', marginBottom: '16px', color: '#dc2626' }}>
              {submitError}
            </div>
          )}

          {/* Durum: İşlemde (teknisyen API) */}
          {!isIslemde && !isTamamlandi && (
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {canGoIslemde && (
                <button type="button" className="rt-btn rt-btn-primary" onClick={handleIslemde} disabled={actionLoading}>
                  <i className="fas fa-tools"></i>
                  İşlemde
                </button>
              )}
            </div>
          )}

          {isIslemde && (
            <>
              {/* Yapılan İşlemler - içeriklerden seç veya manuel */}
              <div className="randevu-test-section" style={{ marginBottom: '24px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-tasks" style={{ color: '#2563eb' }}></i>
                  Yapılan İşlemler
                </h3>
                {yapilanIslemler.length > 0 && (
                  <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontSize: '14px', color: '#475569' }}>
                    {yapilanIslemler.map((op, i) => (
                      <li key={i}>{safeText(op.islemAdi)} {op.fiyat != null && op.fiyat > 0 && ` · ${op.fiyat} ₺`} {op.aciklama && ` – ${op.aciklama}`}</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                  <button type="button" className="rt-btn rt-btn-primary" onClick={() => setIsIslemSecModalOpen(true)} disabled={actionLoading}>
                    <i className="fas fa-list"></i>
                    İşlemden Seç
                  </button>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>veya manuel:</span>
                  <input type="text" placeholder="İşlem adı" value={newOperation.islemAdi} onChange={(e) => setNewOperation((o) => ({ ...o, islemAdi: e.target.value }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '160px', fontSize: '14px' }} />
                  <input type="text" placeholder="Açıklama" value={newOperation.aciklama} onChange={(e) => setNewOperation((o) => ({ ...o, aciklama: e.target.value }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '140px', fontSize: '14px' }} />
                  <button type="button" className="rt-btn rt-btn-outline rt-btn-sm" onClick={handleAddOperation} disabled={actionLoading || !newOperation.islemAdi?.trim()}>Ekle</button>
                </div>
              </div>

              {/* Değiştirilen Parçalar - içeriklerden seç veya manuel */}
              <div className="randevu-test-section" style={{ marginBottom: '24px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-cubes" style={{ color: '#059669' }}></i>
                  Değiştirilen Parçalar
                </h3>
                {degistirilenParcalar.length > 0 && (
                  <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontSize: '14px', color: '#475569' }}>
                    {degistirilenParcalar.map((p, i) => (
                      <li key={i}>{safeText(p.parcaAdi)} {p.parcaKodu && ` (${p.parcaKodu})`} – Adet: {p.adet || 1} {p.fiyat != null && p.fiyat > 0 && ` · ${p.fiyat} ₺`}</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                  <button type="button" className="rt-btn rt-btn-success" onClick={() => setIsParcaSecModalOpen(true)} disabled={actionLoading}>
                    <i className="fas fa-list"></i>
                    Parçadan Seç
                  </button>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>veya manuel:</span>
                  <input type="text" placeholder="Parça adı" value={newPart.parcaAdi} onChange={(e) => setNewPart((p) => ({ ...p, parcaAdi: e.target.value }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '120px', fontSize: '14px' }} />
                  <input type="text" placeholder="Kod" value={newPart.parcaKodu} onChange={(e) => setNewPart((p) => ({ ...p, parcaKodu: e.target.value }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '80px', fontSize: '14px' }} />
                  <IntegerInput min={1} value={newPart.adet} onChange={(v) => setNewPart((p) => ({ ...p, adet: v }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '60px', fontSize: '14px' }} />
                  <input type="number" min={0} step="0.01" placeholder="Fiyat" value={newPart.fiyat} onChange={(e) => setNewPart((p) => ({ ...p, fiyat: e.target.value }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '85px', fontSize: '14px' }} />
                  <button type="button" className="rt-btn rt-btn-outline rt-btn-sm" onClick={handleAddPart} disabled={actionLoading || !newPart.parcaAdi?.trim()}>Ekle</button>
                </div>
              </div>

              {/* İlave İşlemler */}
              <div className="randevu-test-section" style={{ marginBottom: '24px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-plus-circle" style={{ color: '#7c3aed' }}></i>
                  İlave İşlemler
                </h3>
                {ilaveIslemler.length > 0 && (
                  <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontSize: '14px', color: '#475569' }}>
                    {ilaveIslemler.map((il, i) => (
                      <li key={i}>{safeText(il.islemAdi)} – {il.adet || 1} adet · {il.fiyat ?? 0} ₺</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <input type="text" placeholder="İlave işlem adı" value={newIlave.islemAdi} onChange={(e) => setNewIlave((p) => ({ ...p, islemAdi: e.target.value }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '140px', fontSize: '14px' }} />
                  <input type="number" min={0} step="0.01" placeholder="Fiyat" value={newIlave.fiyat} onChange={(e) => setNewIlave((p) => ({ ...p, fiyat: e.target.value }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '90px', fontSize: '14px' }} />
                  <IntegerInput min={1} placeholder="Adet" value={newIlave.adet} onChange={(v) => setNewIlave((p) => ({ ...p, adet: v }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '70px', fontSize: '14px' }} />
                  <button type="button" className="rt-btn rt-btn-outline rt-btn-sm" onClick={handleAddIlaveIslem} disabled={actionLoading || !newIlave.islemAdi?.trim()}>İlave Ekle</button>
                </div>
              </div>

              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <h3 style={{ marginBottom: '12px' }}>Randevuyu Tamamla</h3>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Randevu notu</label>
                  <textarea value={aciklama} onChange={(e) => setAciklama(e.target.value)} placeholder="Not..." rows={2} style={{ width: '100%', maxWidth: '400px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={garantiDahilinde} onChange={(e) => setGarantiDahilinde(e.target.checked)} />
                    Garanti dahilinde
                  </label>
                </div>
                {!garantiDahilinde && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Müşteriye fatura edilen</label>
                    <input type="text" value={musteriyeFaturaEdilen} onChange={(e) => setMusteriyeFaturaEdilen(e.target.value)} placeholder="Örn: 150.00 TL" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '200px' }} />
                  </div>
                )}
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                  Konum: verify-otp için yukarıdaki varsayılan test konumu kullanılır {defaultLat && defaultLng ? `(${defaultLat}, ${defaultLng})` : '(boş bırakılırsa gönderilmez)'}.
                </p>
                <div style={{ marginBottom: '12px' }}>
                  <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={(e) => setPhotoFiles((prev) => [...prev, ...Array.from(e.target.files || [])].slice(0, 20))} style={{ display: 'none' }} />
                  <button type="button" className="rt-btn rt-btn-outline" onClick={() => photoInputRef.current?.click()}>
                    <i className="fas fa-camera"></i>
                    Fotoğraf Ekle
                  </button>
                  {photoFiles.length > 0 && <span style={{ marginLeft: '12px', fontSize: '13px', color: '#64748b' }}>{photoFiles.length} dosya</span>}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                  <button type="button" className="rt-btn rt-btn-success" onClick={() => openFinishModal('bitir')}>
                    <i className="fas fa-check-circle"></i>
                    Servis Kaydını Bitir
                  </button>
                  <button type="button" className="rt-btn rt-btn-primary" onClick={() => openFinishModal('yeniRandevu')}>
                    <i className="fas fa-calendar-plus"></i>
                    Yeni Randevu Gerekli
                  </button>
                </div>
              </div>
            </>
          )}

          {isTamamlandi && (
            <p style={{ color: '#059669', fontWeight: 500 }}>
              <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
              Bu randevu tamamlanmıştır.
            </p>
          )}
        </div>
      ) : null}

      {apt && (
        <>
          <IslemSecModal
            isOpen={isIslemSecModalOpen}
            onClose={() => setIsIslemSecModalOpen(false)}
            onSeçilenleriEkle={handleIslemSecilenleriEkle}
          />
          <ParcaSecModal
            isOpen={isParcaSecModalOpen}
            onClose={() => setIsParcaSecModalOpen(false)}
            servisKaydiId={getIdsFromAppointment(apt).srId}
            onSeçilenleriEkle={handleParcaSecilenleriEkle}
          />
        </>
      )}

      {showOtpModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={(e) => e.target === e.currentTarget && setShowOtpModal(false)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', minWidth: '320px', maxWidth: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '16px' }}>OTP Doğrulama</h3>
            <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>{otpLoading ? 'OTP alınıyor...' : 'Testte OTP otomatik alınır ve kutuya yazılır. Onayla\'ya basınca doğrulama yapılır.'}</p>
            <input
              type="text"
              placeholder={otpLoading ? 'OTP alınıyor...' : 'OTP kodu'}
              value={otpValue}
              onChange={(e) => setOtpValue(e.target.value)}
              maxLength={6}
              readOnly={otpLoading}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px', fontSize: '18px', letterSpacing: '4px', textAlign: 'center' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" className="rt-btn rt-btn-outline" onClick={() => { setShowOtpModal(false); setFinishAction(null); }}>İptal</button>
              <button type="button" className="rt-btn rt-btn-primary" onClick={handleFinishSubmit} disabled={actionLoading || otpLoading}>{actionLoading ? 'Gönderiliyor...' : otpLoading ? 'OTP alınıyor...' : 'Onayla'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RandevuTestPage;
